// Account menu in the site header and on the profile page. The panel is the
// club dashboard's avatar menu (pages/manage/ui.js Popover + MenuItem): the
// person's name on top, muted lucide icons, groups split by hairlines.
//
// In the app (Profile tab) the tabs already cover profile and trips, so the
// menu keeps what the tabs don't: Refer & earn, Privacy (a native screen),
// Language and Log out.
import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { CalendarPlus, Coins, Compass, Gift, Globe, LogIn, LogOut, Menu, Shield, User } from "lucide-react";
import PersonOutlinedIcon from "@mui/icons-material/PersonOutlined";
import { useGlobalState } from "../../../GlobalStates";
import { useIsMobile } from "../../../hooks/responsive";
import { formatCurrency } from "../../../utils/currency";
import { ImageResizable } from "../../elements/ImageResizeable";
import { LanguagePortal } from "../header";
import { MenuItem, Popover } from "../../../pages/manage/ui";
import { useMyClubs } from "../../../services/club";
import { hasPlansToManage } from "../../../utils/clubPlans";

const Separator = () => <div className="tw--mx-1.5 tw-my-1 tw-h-px tw-bg-[var(--hairline)]" />;

export const MenuButton = ({ handleClick, handleClose, handleProfileClick, dark = false }) => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const [user, setUser] = useGlobalState("user");
  const location = useLocation();
  const navigate = useNavigate();
  const isInApp = window.isInApp;
  const triggerRef = useRef(null);
  const [languageModalOpen, setLanguageModalOpen] = useState(false);
  const { data: clubsData } = useMyClubs(Boolean(user?.id));
  const planLabel = hasPlansToManage(clubsData?.clubs)
    ? t("club_manage_plans", { defaultValue: "Manage plans" })
    : t("club_plan_something_fun", { defaultValue: "Plan something fun" });

  // Callers (site header, mobile bar) used to own the open state; it lives
  // here now, and their handlers still get called so they stay in sync.
  const [open, setOpen] = useState(false);
  const close = () => {
    setOpen(false);
    handleClose?.();
  };
  const toggle = (e) => {
    setOpen((v) => !v);
    handleClick?.(e);
  };
  const go = (fn) => () => {
    close();
    fn();
  };

  const logout = () => {
    localStorage.removeItem("USER_ID");
    localStorage.removeItem("JWT_TOKEN");
    localStorage.removeItem("locale");
    localStorage.removeItem("currency");
    localStorage.removeItem("community");
    window.location.href = "/";
    window.postMessage("logout");

    setUser({});
  };

  const name = user?.display_name || user?.first_name || "";
  const languageItem = (
    <MenuItem icon={Globe} label={t("language", { defaultValue: "Language" })} onClick={go(() => setLanguageModalOpen(true))} />
  );
  const referItem = <MenuItem icon={Gift} label={t("refer_and_earn", { defaultValue: "Refer & earn" })} onClick={go(() => navigate("/my-referrals"))} />;
  const logoutItem = <MenuItem icon={LogOut} label={t("logout", { defaultValue: "Log out" })} onClick={logout} />;

  const panel = !user?.id ? (
    <MenuItem icon={LogIn} label={t("login", { defaultValue: "Login" })} onClick={go(() => navigate({ pathname: "/login", search: "?reroute=/" }))} />
  ) : isInApp ? (
    <>
      {name && <p className="tw-px-2.5 tw-pb-1 tw-pt-2 tw-text-[13px] tw-text-muted-foreground">{name}</p>}
      {referItem}
      <MenuItem icon={Shield} label={t("privacy", { defaultValue: "Privacy" })} onClick={go(() => window.postMessage("navigateToPrivacy"))} />
      {languageItem}
      <Separator />
      {logoutItem}
    </>
  ) : (
    <>
      {name && <p className="tw-px-2.5 tw-pb-1 tw-pt-2 tw-text-[13px] tw-text-muted-foreground">{name}</p>}
      <MenuItem icon={User} label={t("profile", { defaultValue: "Profile" })} onClick={go(() => (handleProfileClick ? handleProfileClick() : navigate("/profile")))} />
      <MenuItem icon={CalendarPlus} label={planLabel} onClick={go(() => navigate("/club/manage/events"))} />
      <MenuItem
        icon={Compass}
        label={t("account_menu_my_trips", { defaultValue: "My trips" })}
        onClick={go(() => {
          if (window.isInApp) window.postMessage("Bookings");
          else navigate("/my-trips");
        })}
      />
      {referItem}
      {/* Balance only — there's no credits page to open. */}
      <div className="tw-flex tw-items-center tw-gap-2.5 tw-px-2.5 tw-py-2 tw-text-[15px]">
        <Coins className="tw-size-4 tw-shrink-0 tw-text-muted-foreground" />
        <span className="tw-min-w-0 tw-flex-1 tw-truncate tw-font-medium">{t("socredits", { defaultValue: "Socredits" })}</span>
        <span className="cs-num tw-shrink-0 tw-text-muted-foreground">{formatCurrency(user.credits || 0)}</span>
      </div>
      <Separator />
      <MenuItem icon={Shield} label={t("privacy", { defaultValue: "Privacy" })} onClick={go(() => navigate("/profile/privacy"))} />
      {user.is_admin && languageItem}
      <Separator />
      {logoutItem}
    </>
  );

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        onClick={toggle}
        className={
          isMobile
            ? location.pathname === "/profile"
              ? "tw-flex tw-size-10 tw-items-center tw-justify-center tw-rounded-full tw-transition-colors active:tw-bg-black/5"
              : "tw-flex tw-items-center tw-p-0"
            : `tw-flex tw-items-center tw-gap-1.5 tw-rounded-full tw-border tw-px-2 tw-py-1.5 tw-transition-shadow hover:tw-shadow-md ${
                dark ? "tw-border-zinc-700 tw-bg-black" : "tw-border-gray-200 tw-bg-white"
              }`
        }
        aria-label={t("club_account_menu", { defaultValue: "Account menu" })}
        aria-haspopup="menu"
        aria-expanded={open ? "true" : undefined}
      >
        {!isMobile && <Menu size={16} strokeWidth={2} className={dark ? "tw-text-zinc-200" : "tw-text-gray-500"} />}

        {isMobile && location.pathname === "/profile" ? (
          <Menu size={24} strokeWidth={1.75} className="tw-text-[#001624]" />
        ) : (
          <div
            className="tw-shrink-0 tw-overflow-hidden tw-rounded-full"
            style={
              isMobile
                ? {
                    height: "2.7rem",
                    width: "2.7rem",
                    marginLeft: 5,
                    backgroundColor: user && user.image ? "#e5e7eb" : "#f3f4f6",
                  }
                : {
                    height: "2rem",
                    width: "2rem",
                    marginLeft: 2,
                    backgroundColor: user && user.image ? "#e5e7eb" : "#f3f4f6",
                  }
            }
          >
            {user && user.image ? (
              <ImageResizable
                style={{
                  height: "100%",
                  width: "100%",
                  objectFit: "cover",
                  ...(isMobile ? { border: "1px solid lightgrey", borderRadius: "50%" } : {}),
                }}
                alt={user.name}
                src={user.image}
                size="s"
              />
            ) : (
              <div
                className="tw-flex tw-h-full tw-w-full tw-items-center tw-justify-center tw-rounded-full"
                style={isMobile ? { border: "1px solid #e5e7eb" } : {}}
              >
                <PersonOutlinedIcon style={{ fontSize: isMobile ? 26 : 20, color: "#6b7280" }} />
              </div>
            )}
          </div>
        )}
      </button>

      <Popover open={open} onClose={close} anchorRef={triggerRef} align="end" width={240} offset={8}>
        <div role="menu">{panel}</div>
      </Popover>

      <LanguagePortal languageModalOpen={languageModalOpen} onHide={() => setLanguageModalOpen(false)} />
    </>
  );
};
